import React, { useState, useEffect } from 'react';
import { Facebook, Twitter, Linkedin, Link as LinkIcon, MessageCircle } from 'lucide-react';
import { shareToFacebook, shareToTwitter, shareToLinkedIn, shareToWhatsApp, copyToClipboard, ShareData } from '../utils/shareUtils';

interface StickyShareBarProps {
    title: string;
    url: string;
}

const StickyShareBar: React.FC<StickyShareBarProps> = ({ title, url }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            // Show after scrolling 300px
            if (window.scrollY > 300) {
                setIsVisible(true);
            } else {
                setIsVisible(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleCopy = async () => {
        const success = await copyToClipboard(url);
        if (success) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const shareData: ShareData = { title, url };

    if (!isVisible) return null;

    return (
        <>
            {/* Desktop Sidebar */}
            <div className="hidden lg:flex fixed left-8 top-1/2 transform -translate-y-1/2 flex-col gap-4 z-40 animate-fade-in">
                <div className="bg-white p-3 rounded-full shadow-lg border border-gray-100 flex flex-col gap-4">
                    <button
                        onClick={() => shareToFacebook(shareData)}
                        className="text-gray-500 hover:text-[#1877F2] transition-colors p-1"
                        title="Compartilhar no Facebook"
                    >
                        <Facebook className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => shareToTwitter(shareData)}
                        className="text-gray-500 hover:text-[#1DA1F2] transition-colors p-1"
                        title="Compartilhar no Twitter"
                    >
                        <Twitter className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => shareToLinkedIn(shareData)}
                        className="text-gray-500 hover:text-[#0A66C2] transition-colors p-1"
                        title="Compartilhar no LinkedIn"
                    >
                        <Linkedin className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => shareToWhatsApp(shareData)}
                        className="text-gray-500 hover:text-[#25D366] transition-colors p-1"
                        title="Compartilhar no WhatsApp"
                    >
                        <MessageCircle className="w-5 h-5" />
                    </button>
                    <div className="h-px bg-gray-200 w-full"></div>
                    <button
                        onClick={handleCopy}
                        className={`text-gray-500 transition-colors p-1 ${copied ? 'text-green-600' : 'hover:text-indigo-600'}`}
                        title="Copiar link"
                    >
                        <LinkIcon className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Mobile Bottom Bar */}
            <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-40 shadow-lg animate-slide-up">
                <div className="flex justify-around items-center max-w-md mx-auto">
                    <span className="text-sm font-medium text-gray-500 mr-2">Compartilhar:</span>
                    <button
                        onClick={() => shareToWhatsApp(shareData)}
                        className="text-[#25D366] p-2"
                        aria-label="Compartilhar no WhatsApp"
                        title="Compartilhar no WhatsApp"
                    >
                        <MessageCircle className="w-6 h-6" />
                    </button>
                    <button
                        onClick={() => shareToFacebook(shareData)}
                        className="text-[#1877F2] p-2"
                        aria-label="Compartilhar no Facebook"
                        title="Compartilhar no Facebook"
                    >
                        <Facebook className="w-6 h-6" />
                    </button>
                    <button
                        onClick={() => shareToTwitter(shareData)}
                        className="text-[#1DA1F2] p-2"
                        aria-label="Compartilhar no Twitter"
                        title="Compartilhar no Twitter"
                    >
                        <Twitter className="w-6 h-6" />
                    </button>
                    <button
                        onClick={() => shareToLinkedIn(shareData)}
                        className="text-[#0A66C2] p-2"
                        aria-label="Compartilhar no LinkedIn"
                        title="Compartilhar no LinkedIn"
                    >
                        <Linkedin className="w-6 h-6" />
                    </button>
                    <button
                        onClick={handleCopy}
                        className={`p-2 ${copied ? 'text-green-600' : 'text-gray-600'}`}
                        aria-label="Copiar link"
                        title="Copiar link"
                    >
                        <LinkIcon className="w-6 h-6" />
                    </button>
                </div>
            </div>
        </>
    );
};

export default StickyShareBar;
